import { MobilePlan } from '@numeo/types-core'
import { MobilePromoCode, MobilePromoCodeStatus } from '@numeo/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Plus, RefreshCw } from 'lucide-react'
import { mobileOfferError, mobileOffersApi } from './api'
import { OfferButton, OfferNotice, OfferSection, storeMark } from './ui'
import { planLabels } from './offerLabels'
import { PromoCodeForm } from './PromoCodeForm'

const dateLabel = (iso: string) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })
const expired = (promo: MobilePromoCode) => promo.expiresAt !== null && Date.parse(promo.expiresAt) <= Date.now()
/** Confirmed uses plus checkouts still holding a use — the same count the apply enforces the cap on. */
const usedUp = (promo: MobilePromoCode) => promo.maxRedemptions !== null && promo.redemptions + promo.pendingUses >= promo.maxRedemptions

function StatusBadge({ promo }: { promo: MobilePromoCode }) {
    const [tone, label] =
        promo.status === MobilePromoCodeStatus.PAUSED
            ? ['bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', 'Paused']
            : expired(promo)
              ? ['bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300', 'Expired']
              : usedUp(promo)
                ? ['bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', 'Used up']
                : ['bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300', 'Active']
    return <span className={`inline-flex whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ${tone}`}>{label}</span>
}

/** Which plans the code discounts, and on which payment methods. */
function PlanCoverage({ promo }: { promo: MobilePromoCode }) {
    const rows = (Object.entries(promo.plans) as Array<[MobilePlan, MobilePromoCode['plans'][MobilePlan]]>).filter(
        ([, targets]) => targets && (targets.appleOfferId || targets.googleOfferId || targets.stripeCouponId)
    )
    const Apple = storeMark.apple
    const Google = storeMark.google
    return (
        <ul className="space-y-1">
            {rows.map(([plan, targets]) => (
                <li key={plan} className="flex items-center gap-2 text-sm">
                    <span className="min-w-20 font-medium">{planLabels[plan]}</span>
                    {targets?.appleOfferId && (
                        <span title="iOS">
                            <Apple />
                        </span>
                    )}
                    {targets?.googleOfferId && (
                        <span title="Android">
                            <Google />
                        </span>
                    )}
                    {targets?.stripeCouponId && <span className="text-xs text-slate-500 dark:text-slate-400">Card</span>}
                </li>
            ))}
        </ul>
    )
}

/** Codes tab: shareable promo codes users type in the app's subscription page. */
export function PromoCodesPage() {
    const [codes, setCodes] = useState<MobilePromoCode[]>([])
    const [busy, setBusy] = useState(true)
    const [error, setError] = useState('')
    // null → no form; 'new' → create; a code → editing it.
    const [editing, setEditing] = useState<'new' | MobilePromoCode | null>(null)
    const [toggling, setToggling] = useState<string | null>(null)
    const sequence = useRef(0)

    const load = useCallback(async () => {
        const request = ++sequence.current
        setBusy(true)
        setError('')
        try {
            const result = await mobileOffersApi.promoCodes()
            if (request === sequence.current) setCodes(result)
        } catch (loadError) {
            if (request === sequence.current) setError(mobileOfferError(loadError))
        } finally {
            if (request === sequence.current) setBusy(false)
        }
    }, [])
    useEffect(() => {
        void load()
    }, [load])

    const saved = (promo: MobilePromoCode) => {
        setCodes((current) => [promo, ...current.filter((existing) => existing.code !== promo.code)])
        setEditing(null)
    }
    const toggle = async (promo: MobilePromoCode) => {
        setToggling(promo.code)
        setError('')
        try {
            const next = promo.status === MobilePromoCodeStatus.ACTIVE ? MobilePromoCodeStatus.PAUSED : MobilePromoCodeStatus.ACTIVE
            const updated = await mobileOffersApi.setPromoCodeStatus(promo.code, next)
            setCodes((current) => current.map((existing) => (existing.code === updated.code ? updated : existing)))
        } catch (toggleError) {
            setError(mobileOfferError(toggleError))
        } finally {
            setToggling(null)
        }
    }

    return (
        <div className="space-y-6">
            <OfferSection
                title="Promo codes"
                description="Share one code (e.g. SUMMER50). A user types it on the app's subscription page to see the discount for their payment method — iOS, Android, or card — and a use is spent only by the purchase they make with it. Each user can use a code once."
                action={
                    <div className="flex gap-2">
                        <OfferButton variant="secondary" disabled={busy} onClick={() => void load()} aria-label="Refresh codes">
                            <RefreshCw size={16} aria-hidden="true" />
                        </OfferButton>
                        <OfferButton disabled={editing !== null} onClick={() => setEditing('new')}>
                            <Plus size={16} aria-hidden="true" />
                            New code
                        </OfferButton>
                    </div>
                }
            >
                {editing === 'new' && <PromoCodeForm onSaved={saved} onCancel={() => setEditing(null)} />}
                {error && <OfferNotice error>{error}</OfferNotice>}
                {busy && codes.length === 0 ? (
                    <div className="h-24 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" role="status" aria-label="Loading codes" />
                ) : codes.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">No promo codes yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-left text-sm">
                            <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                <tr>
                                    <th className="py-2 pr-4 font-medium">Code</th>
                                    <th className="py-2 pr-4 font-medium">Plans</th>
                                    <th className="py-2 pr-4 font-medium">Uses</th>
                                    <th className="py-2 pr-4 font-medium">Expires</th>
                                    <th className="py-2 pr-4 font-medium">Status</th>
                                    <th className="py-2 font-medium">
                                        <span className="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {codes.map((promo) =>
                                    editing !== 'new' && editing?.code === promo.code ? (
                                        <tr key={promo.code}>
                                            <td colSpan={6} className="py-3">
                                                <PromoCodeForm initial={promo} onSaved={saved} onCancel={() => setEditing(null)} />
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr key={promo.code} className="align-top">
                                            <td className="py-3 pr-4 font-mono font-semibold">{promo.code}</td>
                                            <td className="py-3 pr-4">
                                                <PlanCoverage promo={promo} />
                                            </td>
                                            <td className="py-3 pr-4 tabular-nums">
                                                {promo.redemptions} / {promo.maxRedemptions ?? '∞'}
                                                {/* A use is held from Subscribe until the purchase confirms it (or the checkout lapses, within a day). */}
                                                {promo.pendingUses > 0 && (
                                                    <span
                                                        className="ml-1 text-xs text-slate-500 dark:text-slate-400"
                                                        title="Checkouts holding a use, not yet confirmed by a purchase"
                                                    >
                                                        · {promo.pendingUses} pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-3 pr-4">{promo.expiresAt ? dateLabel(promo.expiresAt) : 'Never'}</td>
                                            <td className="py-3 pr-4">
                                                <StatusBadge promo={promo} />
                                            </td>
                                            <td className="py-3">
                                                <div className="flex justify-end gap-2">
                                                    <OfferButton variant="ghost" disabled={editing !== null} onClick={() => setEditing(promo)}>
                                                        Edit
                                                    </OfferButton>
                                                    <OfferButton variant="secondary" disabled={toggling === promo.code} onClick={() => void toggle(promo)}>
                                                        {promo.status === MobilePromoCodeStatus.ACTIVE ? 'Pause' : 'Resume'}
                                                    </OfferButton>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </OfferSection>
        </div>
    )
}
